import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import useDimensions from 'react-use-dimensions';
import VisibilitySensor from 'react-visibility-sensor/visibility-sensor';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';
import { connect } from "react-redux";

import ProgressBar from './progress-bar';
import SummaryDesktop from "./summary-desktop";
import SummaryMobile from "./summary-mobile";
import StickyFooter from "./sticky-footer";
import ConfiguratorActionButtons from "./configurator-action-buttons";
import CustomRequestOption from "./custom-request-option";
import OptionRow from "./option-row";
import QuantityConfiguratorMobile from "./quantity-configuration-mobile";
import VariationImage from "./variation-image";
import mapDispatchToProps from "../../lib/map-dispatch-to-props";
import * as configuratorActions from "../../actions/configurator-actions";
import * as projectActions from "../../actions/projects";
import * as modalActions from "../../actions/modals";
import {
  hideElementsForMobileConfigurator,
  getDimension
} from "../../lib/helpers";
import { getEstimatedTotal } from "../../selectors/configurator-selectors";

const STICKY_HEIGHT_THRESHOLD = 500;

const sortOptionTypes = (optionTypes) => {
  //
  // Always move the Sectional Layout to the top, if it exists
  //
  const sectionalLayout = optionTypes.find(ot => {
    return ot.get('name') === 'sectional_layout';
  });

  if (sectionalLayout) {
    const sectionalLayoutIndex = optionTypes.indexOf(sectionalLayout);

    optionTypes.splice(sectionalLayoutIndex, 1);
    optionTypes.splice(0, 0, sectionalLayout);
  }

  //
  // Move the Daybed Orientation to right below the Daybed Width,
  // if both exist
  //
  const daybedWidth = optionTypes.find(ot => {
    return ot.get('name') === 'daybed_width';
  });

  if (!daybedWidth) {
    return optionTypes;
  }

  const daybedOrientation = optionTypes.find(ot => {
    return ot.get('name') === 'daybed_orientation';
  });

  if (!daybedOrientation) {
    return optionTypes;
  }

  const daybedWidthIndex = optionTypes.indexOf(daybedWidth);
  const daybedOrientationIndex = optionTypes.indexOf(daybedOrientation);

  optionTypes.splice(daybedOrientationIndex, 1);
  optionTypes.splice(daybedWidthIndex+1, 0, daybedOrientation);

  return optionTypes;
}

export const Configurator = props => {
  const {
    clientId,
    creatingProject,
    customRequest,
    depth,
    estimatedTotalPrice,
    height,
    isEditing,
    isStaffUser,
    lineItemId,
    optionTypes,
    persistVariationImageURL,
    product,
    productId,
    projectName,
    projectSlug,
    projectUpdating,
    quantityCount,
    selectedVariant,
    selections,
    sideADimension,
    sideBDimension,
    sideCDimension,
    sectionalLayoutIsCustom,
    userId,
    userRole,
    userStarted,
    variationImageURL,
    width,
    comDetail,
    actions: {
      configuratorActions: {
        customRequestChanged,
        customValueChanged,
        sideADimensionChanged,
        sideBDimensionChanged,
        sideCDimensionChanged,
        sectionalLayoutIsCustomChanged,
        decreaseQuantity,
        fetchProduct,
        increaseQuantity,
        optionSelected,
        setIsEditing,
        startConfigurator,
        updatedVariationImageURL,
        editComDetailField,
        loadPreviousState,
      },
      projectActions: {
        fetchProject,
        updateProjectItem,
      },
      modalActions: { openModal }
    }
  } = props;
  const [mobile] = useState(isMobile());
  const [currentStep, setCurrentStep] = useState(0);
  const [topRef, { y: topYDimension }] = useDimensions();

  const isLoggedIn = !!userId;

  let totalSteps = optionTypes.size;
  if (userRole === 'trade_user') {
    totalSteps = optionTypes.size + 1;
  }

  let comRequiredYardage = props.comRequiredYardage;
  if (comRequiredYardage <= 0) {
    comRequiredYardage = "N/A";
  }

  useEffect(() => {
    if (projectSlug && lineItemId) {
      setIsEditing();

      fetchProject(projectSlug, lineItemId, clientId)
        .then(res => {
          fetchProduct(productId, lineItemId);
        })
        .then(res => {
          loadPreviousState(productId);
        })
        .then(res => {
          hideElementsForMobileConfigurator();
          startConfigurator();
          const prevModal = localStorage.getItem("prevModal");
          if (prevModal) {
            openModal(prevModal);
            localStorage.removeItem("prevModal");
          }
        });
    } else {
      fetchProduct(productId)
        .then(res => {
          loadPreviousState(productId);
        })
        .then(res => {
          const prevModal = localStorage.getItem("prevModal");
          if (prevModal) {
            openModal(prevModal)
            localStorage.removeItem("prevModal");
          }
        });
    }
  }, []);

  if (product.isEmpty()) return null;
  //if (!isLoggedIn) return null;

  const {
    displayCategory,
    productType,
    optionValueRestrictions,
    hasVariationImages
  } = product.toObject();

  const hasGeneralCustomizationNotice =
    SHOW_CUSTOMIZATION_NOTE &&
    GENERAL_CUSTOMIZATION_NOTICE_PRODUCT_TYPES.indexOf(productType) > -1;
  const hasSofaCustomizationNotice =
    SHOW_CUSTOMIZATION_NOTE && productType === "sofas" && userRole === 'trade_user';

  if (!userStarted && mobile) {
    const onStartClick = e => {
      e.preventDefault();

      hideElementsForMobileConfigurator();
      startConfigurator();
    };

    // If the user is not signed in, show nothing;
    // there's already a button at the top of the screen
    // ACTUALLY, I think this needs to be here?
    // if (userId === '') {
    //   return null;
    // }

    // Apparently we need this to be here, even though it's display: none,
    // so that the other start quote button works...
    return (
      <div className="configurator-wrapper" style={{ display: 'none' }}>
        <button
          onClick={onStartClick}
          className="large center start-configurator"
        >
          Start Your Quote
        </button>
      </div>
    );
  }

  const onCustomRequestChange = e => {
    customRequestChanged(e.currentTarget.value);
  };

  const onVisibilityChange = (isVisible, index) => {
    if (index === currentStep + 1 && isVisible === true) {
      setCurrentStep(currentStep + 1);
    } else if (index === currentStep - 1 && isVisible === true) {
      setCurrentStep(currentStep - 1);
    }
  };

  let optionTypesAndCustomRequest;

  if (optionTypes) {
    optionTypesAndCustomRequest = optionTypes.valueSeq().toArray();

    if (userRole === 'trade_user') {
      const customRequest = Immutable.Map({
        id: 0,
        name: 'custom_request',
        displayName: 'Custom Requests',
      });

      optionTypesAndCustomRequest.push(customRequest);

      // if (displayCategory === 'sectionals') {
      //   optionTypesAndCustomRequest.unshift(customRequest);
      // } else {
      //   optionTypesAndCustomRequest.push(customRequest);
      // }
    }

    optionTypesAndCustomRequest = sortOptionTypes(optionTypesAndCustomRequest);
  }

  const getNextButtonText = () => {
    // Hide next button on last step
    if (currentStep === totalSteps - 1) return null;

    const nextOptionType = optionTypesAndCustomRequest[currentStep + 1];

    return `Continue to ${nextOptionType.get('displayName')}`;
  };

  const getNextButton = () => {
    if (currentStep === totalSteps - 1) {
      return (
        <ConfiguratorActionButtons
          plain
          clientId={clientId}
          creatingProject={creatingProject}
          currentProjectName={projectName}
          isEditing={isEditing}
          lineItemId={lineItemId}
          openModal={openModal}
          projectSlug={projectSlug}
          projectUpdating={projectUpdating}
          updateProjectItem={updateProjectItem}
          isLoggedIn={isLoggedIn}
          userRole={userRole}
        />
      );
    }

    return null;
  };

  const getNextScrollTarget = () => {
    return `#option-type-${currentStep + 1}`;
  };

  const getPreviousScrollTarget = () => {
    if (currentStep === 0) return null;

    return `#option-type-${currentStep - 1}`;
  };

  let isComExist = false;
  const fabricOption = optionTypesAndCustomRequest.find(c => c.get('name') === 'fabric');
  if (fabricOption) {
    const comOptions = fabricOption.get('optionValues').filter(f => f.getIn(["metaData", "collection"]) === FABRIC_CUSTOMER_OWNED);
    isComExist = comOptions.size > 0;
  }

  return (
    <div className="configurator-wrapper" id="configurator-container" ref={topRef}>
      <div className="product-quote-image mobile-only">
        <VariationImage
          src={variationImageURL}
          imgixParams={{ w: 1200 }}
          initialSrc={product.get("primaryImageUrl")}
          updateURL={updatedVariationImageURL}
          hasError={!persistVariationImageURL}
          hasVariationImages={hasVariationImages}
        />
      </div>

      <div className="product-customize-options">
        {mobile && (
          <SummaryMobile
            selections={selections}
            estimatedTotalPrice={estimatedTotalPrice}
            product={product}
            selectedVariant={selectedVariant}
            sideADimension={sideADimension}
            sideBDimension={sideBDimension}
            sideCDimension={sideCDimension}
            sectionalLayoutIsCustom={sectionalLayoutIsCustom}
          />
        )}

        <div className="col-8-12 configurator-options">
          {hasGeneralCustomizationNotice && (
            <div
              className="customization-notice-wrapper desktop-only"
              id="customization-notice"
            >
              <p className="customization-notice-headline">
                Customization Note
              </p>
              <p className="customization-notice-subheader">
                Not all customizations may be reflected in the preview.
              </p>
            </div>
          )}
          <div className="product-options">
            <QuantityConfiguratorMobile
              quantityCount={quantityCount}
              increaseQuantity={increaseQuantity}
              decreaseQuantity={decreaseQuantity}
            />

            {optionTypesAndCustomRequest &&
              optionTypesAndCustomRequest.map((optionTypeOrCustomRequest, index) => {
                const { id } = optionTypeOrCustomRequest.toObject();
                let child;

                if (optionTypeOrCustomRequest.get('name') === 'custom_request') {
                  child = (
                    <CustomRequestOption
                      onChange={onCustomRequestChange}
                      value={customRequest}
                      id={`option-type-${index}`}
                    />
                  );
                } else {
                  const selection = selections.find(s => {
                    return id === s.get("optionTypeId");
                  });

                  child = (
                    <OptionRow
                      sideADimension={sideADimension}
                      sideBDimension={sideBDimension}
                      sideCDimension={sideCDimension}
                      sectionalLayoutIsCustom={sectionalLayoutIsCustom}
                      sideADimensionChanged={sideADimensionChanged}
                      sideBDimensionChanged={sideBDimensionChanged}
                      sideCDimensionChanged={sideCDimensionChanged}
                      sectionalLayoutIsCustomChanged={sectionalLayoutIsCustomChanged}
                      customValueChanged={customValueChanged}
                      editComDetailField={editComDetailField}
                      comDetail={comDetail}
                      isStaffUser={isStaffUser}
                      userRole={userRole}
                      openModal={openModal}
                      optionId={id}
                      optionSelected={optionSelected}
                      optionType={optionTypeOrCustomRequest}
                      optionValueRestrictions={optionValueRestrictions}
                      optionValues={optionTypeOrCustomRequest.get("optionValues")}
                      productType={productType}
                      quantityCount={quantityCount}
                      selectedVariant={selectedVariant}
                      selection={selection}
                      selections={selections}
                      id={`option-type-${index}`}
                      product={product}
                      isEditing={isEditing}
                      mobile={mobile}
                    />
                  );
                }

                return (
                  <VisibilitySensor
                    offset={{ top: STICKY_HEIGHT_THRESHOLD }}
                    minTopValue={STICKY_HEIGHT_THRESHOLD}
                    onChange={(visible) => onVisibilityChange(visible, index)}
                    partialVisibility
                    key={id}
                  >
                    {child}
                  </VisibilitySensor>
                );
              })}

            {!sectionalLayoutIsCustom && (
              <div className="product-options-row option-columns two-columns com-required-yardage-row mobile-only">
                <div className="product-options-wrapper-mobile mobile-only">
                  <div className="option-selection-wrapper">
                    <p className="option-name">COM Required Yardage</p>
                    <p className="option-value">
                      {comRequiredYardage}
                      {" "}
                      {quantityCount > 1 && ` × ${quantityCount}`}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {hasGeneralCustomizationNotice && (
            <div
              className="customization-notice-wrapper mobile-only"
              id="customization-notice"
            >
              <p className="customization-notice-headline">
                Customization Note
              </p>
              <p className="customization-notice-subheader">
                Not all customizations may be reflected in the preview.
              </p>
            </div>
          )}
          {hasSofaCustomizationNotice && (
            <div className="customization-notice-wrapper mobile-only">
              <p className="customization-notice-headline">
                Customization Note
              </p>
              <p className="customization-notice-subheader">
                You may order this sofa as a sectional. Please provide your
                details in the Custom Request field at the bottom of the page
                or
                {" "}
                <a href="/contact" className="customization-notice-link">
                  contact us
                </a>.
              </p>
            </div>
          )}

          {mobile && (
            <ConfiguratorActionButtons
              clientId={clientId}
              creatingProject={creatingProject}
              currentProjectName={projectName}
              isEditing={isEditing}
              lineItemId={lineItemId}
              openModal={openModal}
              projectSlug={projectSlug}
              projectUpdating={projectUpdating}
              updateProjectItem={updateProjectItem}
              isLoggedIn={isLoggedIn}
            />
          )}
        </div>

        {!mobile && (
          <SummaryDesktop
            sideADimension={sideADimension}
            sideBDimension={sideBDimension}
            sideCDimension={sideCDimension}
            sectionalLayoutIsCustom={sectionalLayoutIsCustom}
            customRequest={customRequest}
            decreaseQuantity={decreaseQuantity}
            depth={depth}
            estimatedTotalPrice={estimatedTotalPrice}
            height={height}
            increaseQuantity={increaseQuantity}
            openModal={openModal}
            product={product}
            variationImageURL={variationImageURL}
            persistVariationImageURL={persistVariationImageURL}
            quantityCount={quantityCount}
            selections={selections}
            width={width}
            updateURL={updatedVariationImageURL}
            hasVariationImages={hasVariationImages}
            selectedVariant={selectedVariant}
            isMobile={mobile}
            comRequiredYardage={comRequiredYardage}
          />
        )}
      </div>

      {(!mobile && optionTypes) && (
        <StickyFooter
          previousButtonProps={{
            'data-scroll': getPreviousScrollTarget()
          }}
          nextButtonProps={{
            'data-scroll': getNextScrollTarget()
          }}
          nextButtonText={getNextButtonText()}
          nextButton={getNextButton()}
          showPreviousButton={!!getPreviousScrollTarget()}
          currentStep={currentStep}
          isBelowTop={topYDimension < STICKY_HEIGHT_THRESHOLD}
          totalSteps={totalSteps}
          middleSection={(
            <ProgressBar
              currentStep={currentStep}
              totalSteps={totalSteps}
            />
          )}
        />
      )}
    </div>
  );
};

Configurator.propTypes = {
  actions: PropTypes.object.isRequired,
  clientId: PropTypes.string,
  creatingProject: PropTypes.bool.isRequired,
  customRequest: PropTypes.string,
  depth: PropTypes.string,
  estimatedTotalPrice: PropTypes.number.isRequired,
  height: PropTypes.string,
  isEditing: PropTypes.bool.isRequired,
  lineItemId: PropTypes.string,
  product: ImmutablePropTypes.map,
  productId: PropTypes.string,
  projectName: PropTypes.string.isRequired,
  projectSlug: PropTypes.string,
  projectUpdating: PropTypes.bool.isRequired,
  quantityCount: PropTypes.number.isRequired,
  selections: ImmutablePropTypes.map,
  userId: PropTypes.string,
  userRole: PropTypes.string,
  userStarted: PropTypes.bool.isRequired,
  width: PropTypes.string,
  optionTypes: ImmutablePropTypes.map,
  variationImageURL: PropTypes.string,
  persistVariationImageURL: PropTypes.bool.isRequired,
  isStaffUser: PropTypes.bool.isRequired,
  selectedVariant: ImmutablePropTypes.map,
  comRequiredYardage: PropTypes.number.isRequired
};

function mapStateToProps(state) {
  const product = state.configuratorStore.get("product");

  const {
    optionTypes,
    creatingProject,
    customRequest,
    isEditing,
    projectName,
    quantityCount,
    selections,
    userStarted,
    selectedVariant,
    variationImageURL,
    persistVariationImageURL,
    isStaffUser,
    comRequiredYardage,
    comDetail,
    sideADimension,
    sideBDimension,
    sideCDimension,
    sectionalLayoutIsCustom,
  } = state.configuratorStore.toObject();

  const estimatedTotalPrice = getEstimatedTotal(state) * quantityCount;
  const height = getDimension(
    "height",
    selectedVariant.get("height"),
    selections
  );
  const depth = getDimension("depth", selectedVariant.get("depth"), selections);
  const width = getDimension("width", selectedVariant.get("width"), selections);
  const { userId, userRole, projects } = state.userStore.toObject();
  const projectUpdating = state.projectsStore.get("updating");

  return {
    sideADimension,
    sideBDimension,
    sideCDimension,
    sectionalLayoutIsCustom,
    product,
    estimatedTotalPrice,
    quantityCount,
    height,
    depth,
    width,
    selections,
    customRequest,
    userStarted,
    projectName,
    creatingProject,
    projects,
    userId,
    userRole,
    isEditing,
    projectUpdating,
    optionTypes,
    variationImageURL,
    persistVariationImageURL,
    isStaffUser,
    selectedVariant,
    comRequiredYardage,
    comDetail
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps(configuratorActions, projectActions, modalActions)
)(Configurator);
